'use client';

/**
 * useChatTabs — the open-tab strip above the conversation.
 *
 * Off by default; each user turns it on in chat Settings. When on, selecting a
 * chat opens it as a tab and the previously open ones stay MOUNTED, so a turn
 * you left running keeps streaming in the background instead of being torn down
 * and re-fetched when you come back.
 *
 * ⚠️ INTERIM PERSISTENCE — same trade-off as `useChatOrganization`: there is no
 * per-user settings row for chat UI preferences yet, so this lives in
 * localStorage, per browser profile. Kept behind this hook so a server-backed
 * store is a drop-in replacement. Do not assume cross-device sync.
 */

import { useCallback, useSyncExternalStore } from 'react';

/**
 * Hard cap on simultaneously mounted chats.
 *
 * Every open tab is a live `useChat` + its own message query, so this is a real
 * resource ceiling, not a style choice. Opening past it evicts the
 * least-recently-used tab — which costs nothing durable: the chat stays in the
 * sidebar, and a turn still running keeps running server-side.
 */
export const MAX_CHAT_TABS = 8;

export interface ChatTab {
  id: string;
  /** Last known title; refreshed from chat data as it loads. */
  title: string;
  /** Epoch ms, for LRU eviction when the cap is hit. */
  lastActiveAt: number;
}

interface ChatTabsState {
  version: 1;
  enabled: boolean;
  tabs: ChatTab[];
}

const STORAGE_KEY = 'meridian:chat-tabs:v1';

const EMPTY_STATE: ChatTabsState = { version: 1, enabled: false, tabs: [] };

function isChatTab(value: unknown): value is ChatTab {
  if (typeof value !== 'object' || value === null) return false;
  const id = Reflect.get(value, 'id');
  const title = Reflect.get(value, 'title');
  const lastActiveAt = Reflect.get(value, 'lastActiveAt');
  return (
    typeof id === 'string' && typeof title === 'string' && typeof lastActiveAt === 'number'
  );
}

function parseState(raw: string | null): ChatTabsState {
  if (!raw) return EMPTY_STATE;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY_STATE;
    const enabled = Reflect.get(parsed, 'enabled');
    const tabsRaw = Reflect.get(parsed, 'tabs');
    const tabs = Array.isArray(tabsRaw) ? tabsRaw.filter(isChatTab) : [];
    return {
      version: 1,
      enabled: enabled === true,
      // Trim rather than trust a persisted list that outgrew the cap.
      tabs: tabs.slice(0, MAX_CHAT_TABS),
    };
  } catch {
    // Corrupt payload — start clean rather than crash the chat page.
    return EMPTY_STATE;
  }
}

// --- Module-level store (shared by the strip, the panes, and settings) ------

let state: ChatTabsState | null = null;
const listeners = new Set<() => void>();

function ensureLoaded(): ChatTabsState {
  if (state) return state;
  if (typeof window === 'undefined') {
    state = EMPTY_STATE;
    return state;
  }
  state = parseState(window.localStorage.getItem(STORAGE_KEY));
  return state;
}

function persist(next: ChatTabsState): void {
  state = next;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Storage full / disabled — keep the in-memory copy so tabs still work
      // this session; nothing user-facing to surface.
    }
  }
  listeners.forEach((l) => l());
}

/**
 * Other browser tabs share this store. Without this, opening Hub twice lets the
 * two windows overwrite each other's tab strip — last write wins, silently.
 * `key === null` is the `localStorage.clear()` case.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    state = parseState(window.localStorage.getItem(STORAGE_KEY));
    listeners.forEach((l) => l());
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ChatTabsState {
  return ensureLoaded();
}

function getServerSnapshot(): ChatTabsState {
  return EMPTY_STATE;
}

export interface UseChatTabsResult {
  /** User preference: is the tab strip on at all? */
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  tabs: ChatTab[];
  /**
   * Open (or re-activate) a chat as a tab. Safe to call on every selection —
   * an already-open chat is only touched for LRU.
   */
  openTab: (chatId: string, title?: string) => void;
  closeTab: (chatId: string) => void;
  /** Refresh a tab's label once the chat's real title is known. */
  setTabTitle: (chatId: string, title: string) => void;
}

const UNTITLED_TAB_LABEL = 'New chat';

/**
 * Next activity stamp for `tabs`.
 *
 * `Date.now()` alone is not enough: several tabs opened inside the same
 * millisecond tie, and the LRU eviction below then picks an arbitrary one — so
 * a chat the user just touched could be the one closed. Forcing the stamp past
 * every existing one keeps the ordering strict.
 */
function nextActivityStamp(tabs: ChatTab[]): number {
  const highest = tabs.reduce((max, tab) => (tab.lastActiveAt > max ? tab.lastActiveAt : max), 0);
  return Math.max(Date.now(), highest + 1);
}

export function useChatTabs(): UseChatTabsResult {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setEnabled = useCallback((enabled: boolean) => {
    const current = ensureLoaded();
    // Turning the strip off closes every tab: leaving them persisted would make
    // re-enabling it restore a stale set from an unrelated session.
    persist({ ...current, enabled, tabs: enabled ? current.tabs : [] });
  }, []);

  const openTab = useCallback((chatId: string, title?: string) => {
    const current = ensureLoaded();
    const now = nextActivityStamp(current.tabs);
    const existing = current.tabs.find((tab) => tab.id === chatId);

    if (existing) {
      persist({
        ...current,
        tabs: current.tabs.map((tab) =>
          tab.id === chatId
            ? { ...tab, lastActiveAt: now, title: title ?? tab.title }
            : tab,
        ),
      });
      return;
    }

    const opened: ChatTab = { id: chatId, title: title ?? UNTITLED_TAB_LABEL, lastActiveAt: now };
    const next = [...current.tabs, opened];

    if (next.length > MAX_CHAT_TABS) {
      // Evict the least recently active tab OTHER than the one just opened.
      const evictable = next.filter((tab) => tab.id !== chatId);
      const oldest = evictable.reduce((a, b) => (a.lastActiveAt <= b.lastActiveAt ? a : b));
      persist({ ...current, tabs: next.filter((tab) => tab.id !== oldest.id) });
      return;
    }

    persist({ ...current, tabs: next });
  }, []);

  const closeTab = useCallback((chatId: string) => {
    const current = ensureLoaded();
    if (!current.tabs.some((tab) => tab.id === chatId)) return;
    persist({ ...current, tabs: current.tabs.filter((tab) => tab.id !== chatId) });
  }, []);

  const setTabTitle = useCallback((chatId: string, title: string) => {
    const current = ensureLoaded();
    const existing = current.tabs.find((tab) => tab.id === chatId);
    if (!existing || existing.title === title) return;
    persist({
      ...current,
      tabs: current.tabs.map((tab) => (tab.id === chatId ? { ...tab, title } : tab)),
    });
  }, []);

  return {
    enabled: snapshot.enabled,
    setEnabled,
    tabs: snapshot.tabs,
    openTab,
    closeTab,
    setTabTitle,
  };
}
